import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Card } from 'react-bootstrap';
import * as Icon from 'react-feather';

function DefaultModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const [fumigationData, setFumigationData] = useState({
        inventory_id: '',
        service_date: '',
        service_time: '',
        status: 'scheduled',
        provider: '',
        sanitary_license: '',
        total_cost: 0,
        invoice_id: '',
        is_paid: false,
        date_paid: '',
        time_paid: '',
        notes: ''
    });

    const [loading, setLoading] = useState(false);

    // Initialize form with fumigation data when modal opens
    useEffect(() => {
        if (props.show && props.fumigationInfo) {
            initializeForm();
        }
    }, [props.show, props.fumigationInfo]);

    const initializeForm = () => {
        const fumigation = props.fumigationInfo;

        // Split service datetime into date and time
        const serviceDateTime = fumigation.service_datetime ? fumigation.service_datetime.split(' ') : ['', ''];
        const serviceDatePart = serviceDateTime[0] || '';
        const serviceTimePart = serviceDateTime[1] ? serviceDateTime[1].slice(0, 5) : ''; // Remove seconds

        // Split date_paid into date and time if exists
        const datePaid = fumigation.date_paid ? fumigation.date_paid.split(' ') : ['', ''];
        const datePaidPart = datePaid[0] || '';
        const timePaidPart = datePaid[1] ? datePaid[1].slice(0, 5) : ''; // Remove seconds

        setFumigationData({
            inventory_id: fumigation.inventory_id || '',
            service_date: serviceDatePart,
            service_time: serviceTimePart,
            status: fumigation.status || 'scheduled',
            provider: fumigation.provider || '',
            sanitary_license: fumigation.sanitary_license || '',
            total_cost: fumigation.total_cost || 0,
            invoice_id: fumigation.invoice_id || '',
            is_paid: fumigation.is_paid || false,
            date_paid: datePaidPart,
            time_paid: timePaidPart,
            notes: fumigation.notes || ''
        });
    };

    const handleChange = (field, value) => {
        setFumigationData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        try {
            // Combine date and time for service_datetime
            const service_datetime = `${fumigationData.service_date} ${fumigationData.service_time}:00`;

            // Combine date and time for date_paid if provided
            let date_paid = null;
            if (fumigationData.date_paid && fumigationData.time_paid) {
                date_paid = `${fumigationData.date_paid} ${fumigationData.time_paid}:00`;
            }

            const payload = {
                inventory_id: fumigationData.inventory_id,
                service_datetime: service_datetime,
                status: fumigationData.status,
                provider: fumigationData.provider || null,
                sanitary_license: fumigationData.sanitary_license || null,
                total_cost: parseFloat(fumigationData.total_cost) || 0,
                invoice_id: fumigationData.invoice_id || null,
                is_paid: fumigationData.is_paid,
                date_paid: date_paid,
                notes: fumigationData.notes || null
            };

            const response = await axios.put(`${apiUrl}/services/fumigation/${props.fumigationInfo.id}`, payload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            // alert('Fumigación actualizada exitosamente');
            props.setData();
            props.onHide();

        } catch (error) {
            console.error('Error updating fumigation:', error);
            alert('Error al actualizar el registro de fumigación');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Editar Fumigación
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Container>
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Information */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información Básica</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Unidad *</Form.Label>
                                        <Form.Select
                                            value={fumigationData.inventory_id}
                                            onChange={(e) => handleChange('inventory_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccionar unidad...</option>
                                            {props.inventoryOptions.map((unit) => (
                                                <option key={unit.id} value={unit.id}>
                                                    {unit.name || unit.inventory_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Estado *</Form.Label>
                                        <Form.Select
                                            value={fumigationData.status}
                                            onChange={(e) => handleChange('status', e.target.value)}
                                            required
                                        >
                                            <option value="scheduled">Programado</option>
                                            <option value="completed">Completado</option>
                                            <option value="cancelled">Cancelado</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Row>

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Fecha del Servicio *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fumigationData.service_date}
                                            onChange={(e) => handleChange('service_date', e.target.value)}
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Hora del Servicio</Form.Label>
                                        <Form.Control
                                            type="time"
                                            value={fumigationData.service_time}
                                            onChange={(e) => handleChange('service_time', e.target.value)}
                                        />
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Provider Information */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información del Proveedor</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Proveedor</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={fumigationData.provider}
                                            onChange={(e) => handleChange('provider', e.target.value)}
                                            placeholder="Nombre del proveedor"
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Licencia Sanitaria</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={fumigationData.sanitary_license}
                                            onChange={(e) => handleChange('sanitary_license', e.target.value)}
                                            placeholder="Número de licencia"
                                        />
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Financial Information */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información Financiera</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Costo Total</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={fumigationData.total_cost}
                                            onChange={(e) => handleChange('total_cost', e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>ID de Factura</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={fumigationData.invoice_id}
                                            onChange={(e) => handleChange('invoice_id', e.target.value)}
                                            placeholder="Número de factura"
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Check
                                            type="checkbox"
                                            label="Pagado"
                                            checked={fumigationData.is_paid}
                                            onChange={(e) => handleChange('is_paid', e.target.checked)}
                                            className="mt-4"
                                        />
                                    </Form.Group>
                                </Row>

                                {fumigationData.is_paid && (
                                    <Row className="mb-3">
                                        <Form.Group as={Col} md={6}>
                                            <Form.Label>Fecha de Pago</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={fumigationData.date_paid}
                                                onChange={(e) => handleChange('date_paid', e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group as={Col} md={6}>
                                            <Form.Label>Hora de Pago</Form.Label>
                                            <Form.Control
                                                type="time"
                                                value={fumigationData.time_paid}
                                                onChange={(e) => handleChange('time_paid', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Additional Notes */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Notas Adicionales</h6>
                            </Card.Header>
                            <Card.Body>
                                <Form.Group>
                                    <Form.Label>Notas</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={fumigationData.notes}
                                        onChange={(e) => handleChange('notes', e.target.value)}
                                        placeholder="Cualquier información adicional relevante..."
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>

                        <div className="text-end">
                            <Button variant="secondary" onClick={props.onHide} className="me-2">
                                Cancelar
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Actualizando...' : 'Actualizar Fumigación'}
                            </Button>
                        </div>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
    );
}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        let edit_fumigation = () => {
            this.props.setData();
            this.setState({ modalShow: false });
        }

        return (
            <ButtonToolbar>
                <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    <Icon.Edit size={14} />
                </Button>
                <DefaultModal
                    show={this.state.modalShow}
                    onHide={edit_fumigation}
                    setData={this.props.setData}
                    fumigationInfo={this.props.fumigationInfo}
                    inventoryOptions={this.props.inventoryOptions}
                />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
